/* =============================================================================
   V8 KONTOR — Custom Animations & Interactions
   ============================================================================= */

(function () {
  'use strict';

  // ─── 1. Header: transparent → opak beim Scrollen ────────────────────────────
  const SCROLL_THRESHOLD = 60;
  let lastScroll = 0;
  let ticking = false;

  function onScroll() {
    lastScroll = window.scrollY;
    if (!ticking) {
      requestAnimationFrame(updateHeader);
      ticking = true;
    }
  }

  function updateHeader() {
    if (lastScroll > SCROLL_THRESHOLD) {
      document.body.classList.add('v8-scrolled');
    } else {
      document.body.classList.remove('v8-scrolled');
    }
    ticking = false;
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  updateHeader();

  // ─── 2. Scroll-Reveal (Intersection Observer) ────────────────────────────────
  const REVEAL_TARGETS = [
    '.card-wrapper',
    '.product-card',
    '[class*="product-grid"] > *',
    '.collection-list__item',
    '.section-header',
    '[class*="section-header"]',
    '.footer__content-top > *',
    '[class*="image-with-text"]',
    '[class*="featured-collection"]',
    '.rich-text',
    '[class*="rich-text"]',
  ];

  function initReveal() {
    const elements = document.querySelectorAll(REVEAL_TARGETS.join(','));

    if (!elements.length || !('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry, i) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('v8-reveal--visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );

    elements.forEach((el, i) => {
      el.classList.add('v8-reveal');
      // Gestaffelte Verzögerung pro Reihe (max 5 Elemente)
      const delay = Math.min(i % 5, 4);
      if (delay > 0) el.classList.add(`v8-reveal--delay-${delay}`);
      observer.observe(el);
    });
  }

  // ─── 3. Zähler-Animation (Counter-Up) ───────────────────────────────────────
  function animateCounter(el) {
    const target = parseInt(el.dataset.v8Count, 10);
    if (!target) return;
    const duration = 1800;
    const start = performance.now();

    function tick(now) {
      const progress = Math.min((now - start) / duration, 1);
      // Ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      el.textContent = Math.round(eased * target).toLocaleString('de-DE');
      if (progress < 1) requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  }

  function initCounters() {
    const counters = document.querySelectorAll('[data-v8-count]');
    if (!counters.length || !('IntersectionObserver' in window)) return;

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            animateCounter(entry.target);
            obs.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.5 }
    );
    counters.forEach((el) => obs.observe(el));
  }

  // ─── 4. Shop-Nav: Dropdown komplett entfernen (Shop = direkter Link) ──────────
  function initShopMega() {
    const headerEl = document.querySelector(
      'header-menu, [header-menu], .header-wrapper, .header, header, #shopify-section-header'
    );
    if (!headerEl) return;

    // Shop-Trigger finden — textContent erfasst auch verschachtelte Spans
    let trigger = null;
    for (const el of headerEl.querySelectorAll('a, button, summary')) {
      if ((el.textContent || '').trim().toLowerCase() === 'shop') {
        trigger = el; break;
      }
    }
    if (!trigger) return;

    // Submenu physisch aus dem DOM entfernen — keine CSS-Kaskade kann es zurückbringen
    const listItem = trigger.closest('.menu-list__list-item') || trigger.parentElement;
    if (listItem) {
      const sub = listItem.querySelector('[ref="submenu[]"], .menu-list__submenu');
      if (sub) sub.remove();
    }

    // aria-haspopup entfernen → Horizon's HeaderMenu-Komponente löst kein Dropdown aus
    trigger.removeAttribute('aria-haspopup');
    trigger.removeAttribute('aria-controls');
    trigger.removeAttribute('aria-expanded');
  }

  // ─── 5. Produkt-Karten: Quick-Add Button erscheint beim Hover ───────────────
  function initCardHover() {
    const cards = document.querySelectorAll('.card-wrapper');
    cards.forEach((card) => {
      const btn = card.querySelector('.quick-add__submit, .card__quick-add');
      if (!btn) return;

      Object.assign(btn.style, {
        opacity: '0',
        transform: 'translateY(8px)',
        transition: 'opacity 200ms ease, transform 200ms ease',
      });

      card.addEventListener('mouseenter', () => {
        btn.style.opacity = '1';
        btn.style.transform = 'translateY(0)';
      });
      card.addEventListener('mouseleave', () => {
        btn.style.opacity = '0';
        btn.style.transform = 'translateY(8px)';
      });
    });
  }

  // ─── 6. Cursor-Glow (subtiler oranger Punkt der dem Cursor folgt) ─────────
  function initCursorGlow() {
    // Nur Desktop
    if (window.matchMedia('(hover: none)').matches) return;

    const glow = document.createElement('div');
    glow.id = 'v8-cursor-glow';
    Object.assign(glow.style, {
      position: 'fixed',
      width: '300px',
      height: '300px',
      borderRadius: '50%',
      background: 'radial-gradient(circle, rgba(232,67,10,0.06) 0%, transparent 70%)',
      pointerEvents: 'none',
      zIndex: '9999',
      transform: 'translate(-50%, -50%)',
      transition: 'opacity 300ms ease',
      opacity: '0',
      top: '0',
      left: '0',
    });
    document.body.appendChild(glow);

    let mouseX = 0, mouseY = 0;
    let glowX = 0, glowY = 0;
    let visible = false;

    document.addEventListener('mousemove', (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
      if (!visible) {
        visible = true;
        glow.style.opacity = '1';
      }
    });

    document.addEventListener('mouseleave', () => {
      visible = false;
      glow.style.opacity = '0';
    });

    function animateGlow() {
      glowX += (mouseX - glowX) * 0.12;
      glowY += (mouseY - glowY) * 0.12;
      glow.style.left = glowX + 'px';
      glow.style.top = glowY + 'px';
      requestAnimationFrame(animateGlow);
    }
    animateGlow();
  }

  // ─── 7. Announcement Bar — Ticker wenn mehrere Items ────────────────────────
  function initAnnouncementTicker() {
    const bar = document.querySelector('.announcement-bar__message-list');
    if (!bar) return;
    const items = bar.querySelectorAll('[class*="announcement"]');
    if (items.length <= 1) return;

    let current = 0;
    setInterval(() => {
      items[current].style.opacity = '0';
      items[current].style.transform = 'translateY(-100%)';
      current = (current + 1) % items.length;
      const next = items[current];
      next.style.transform = 'translateY(100%)';
      next.style.opacity = '0';
      requestAnimationFrame(() => {
        next.style.transition = 'opacity 400ms ease, transform 400ms ease';
        next.style.opacity = '1';
        next.style.transform = 'translateY(0)';
      });
    }, 4000);
  }

  // ─── 8. Smooth Float-Hover auf Cat-Tiles und Showcase-Items ────────────────
  function initTilt3D() {
    if (window.matchMedia('(hover: none)').matches) return;

    document.querySelectorAll('.v8-cat-tile, .v8-showcase__item').forEach(function(card) {
      card.style.willChange = 'transform';
      card.addEventListener('mouseenter', function() {
        card.classList.add('v8-tile--lifted');
      });
      card.addEventListener('mouseleave', function() {
        card.classList.remove('v8-tile--lifted');
      });
    });
  }

  // ─── 9. Showcase: Fallback-Spotlight für Browser ohne :has() ────────────────
  function initShowcaseSpotlight() {
    var grid = document.getElementById('v8-showcase-grid');
    if (!grid) return;
    // Nur aktivieren wenn :has() nicht unterstützt wird
    try { document.querySelector(':has(*)'); return; } catch(e) {}

    var items = grid.querySelectorAll('.v8-showcase__item');
    items.forEach(function(item) {
      item.addEventListener('mouseenter', function() {
        items.forEach(function(other) {
          if (other !== item) other.style.opacity = '0.28';
        });
      });
      item.addEventListener('mouseleave', function() {
        items.forEach(function(other) { other.style.opacity = ''; });
      });
    });
  }

  // ─── 10. Service-Card Accordion ─────────────────────────────────────────────
  function initServiceCards() {
    document.querySelectorAll('.v8-service-card__trigger').forEach(function(trigger) {
      trigger.addEventListener('click', function() {
        var card = trigger.closest('.v8-service-card');
        var isOpen = card.classList.contains('v8-service-card--open');
        document.querySelectorAll('.v8-service-card--open').forEach(function(c) {
          c.classList.remove('v8-service-card--open');
          c.querySelector('.v8-service-card__trigger').setAttribute('aria-expanded', 'false');
        });
        if (!isOpen) {
          card.classList.add('v8-service-card--open');
          trigger.setAttribute('aria-expanded', 'true');
        }
      });
    });
  }

  // ─── Fahrzeug-Dropdown-Selector ───────────────────────────────────────────────
  function initVehicleDropdown() {
    const markeEl  = document.getElementById('v8Marke');
    const modellEl = document.getElementById('v8Modell');
    const submitEl = document.getElementById('v8VehSubmit');
    const resetEl  = document.getElementById('v8VehReset');
    if (!markeEl) return;

    // Fahrzeug → Lochkreis-Tag Mapping
    const DATA = {
      RAM: {
        '1500 Gen 4+ (2019–heute)': 'lochkreis:6x139.7',
        '1500 Gen 3 (2009–2018)':   'lochkreis:5x139.7',
        '2500 / 3500 (alle)':       'lochkreis:8x165.1',
        'TRX (2021–heute)':         'lochkreis:6x139.7',
      },
      Ford: {
        'F-150 (2015–heute)':       'lochkreis:6x135',
        'F-150 (2004–2014)':        'lochkreis:6x135',
        'Super Duty F-250/F-350':   'lochkreis:8x170',
      },
      GMC: {
        'Sierra 1500 (2014–heute)': 'lochkreis:6x139.7',
        'Sierra 2500/3500':         'lochkreis:8x165.1',
      },
      Chevrolet: {
        'Silverado 1500 (2014–heute)': 'lochkreis:6x139.7',
        'Silverado 2500/3500':         'lochkreis:8x165.1',
      },
      Jeep: {
        'Wrangler JL (2018–heute)': 'lochkreis:5x127',
        'Wrangler JK (2007–2018)':  'lochkreis:5x127',
        'Gladiator JT (alle)':      'lochkreis:5x127',
      },
    };

    function fillModell(marke) {
      modellEl.innerHTML = '<option value="">Modell wählen …</option>';
      if (!marke || !DATA[marke]) { modellEl.disabled = true; return; }
      Object.keys(DATA[marke]).forEach(m => {
        const o = document.createElement('option');
        o.value = DATA[marke][m];
        o.textContent = m;
        modellEl.appendChild(o);
      });
      modellEl.disabled = false;
    }

    markeEl.addEventListener('change', () => {
      fillModell(markeEl.value);
      submitEl.disabled = true;
    });

    modellEl.addEventListener('change', () => {
      submitEl.disabled = !modellEl.value;
    });

    submitEl.addEventListener('click', () => {
      const tag = modellEl.value;
      if (!tag) return;
      // Klassisches Shopify Tag-URL-Format — funktioniert ohne Search & Discovery Konfiguration
      window.location.href = '/collections/felgen/' + encodeURIComponent(tag);
    });

    resetEl.addEventListener('click', () => {
      window.location.href = '/collections/felgen';
    });

    // Aktiven Filter aus klassischer Tag-URL wiederherstellen (/collections/felgen/lochkreis:6x139.7)
    const pathParts = window.location.pathname.split('/');
    const activeTag = pathParts[pathParts.length - 1]
      ? decodeURIComponent(pathParts[pathParts.length - 1])
      : null;
    if (activeTag && activeTag.startsWith('lochkreis:') && resetEl) {
      resetEl.classList.add('v8-visible');
      outer: for (const [marke, modelle] of Object.entries(DATA)) {
        for (const [modell, tag] of Object.entries(modelle)) {
          if (tag === activeTag) {
            markeEl.value = marke;
            fillModell(marke);
            modellEl.value = tag;
            submitEl.disabled = false;
            break outer;
          }
        }
      }
    }
  }

  // ─── Subnav: Availability / Price / Sort Controls ───────────────────────────
  function initSubnavControls() {
    const params = new URLSearchParams(window.location.search);
    const basePath = window.location.pathname;

    // Availability chip — toggle filter.v.availability
    const availChip = document.getElementById('v8AvailChip');
    if (availChip) {
      const isActive = params.get('filter.v.availability') === '1';
      if (isActive) {
        availChip.classList.add('v8-subnav-avail--active');
        // clicking again removes the filter
        const without = new URLSearchParams(params);
        without.delete('filter.v.availability');
        const qs = without.toString();
        availChip.href = basePath + (qs ? '?' + qs : '');
      } else {
        const with_ = new URLSearchParams(params);
        with_.set('filter.v.availability', '1');
        availChip.href = basePath + '?' + with_.toString();
      }
    }

    // Price preset select
    const priceSelect = document.getElementById('v8PriceSelect');
    if (priceSelect) {
      // Detect current price filter from URL
      const lte = params.get('filter.v.price.lte');
      const gte = params.get('filter.v.price.gte');
      if (lte === '300' && !gte) priceSelect.value = 'lte:300';
      else if (gte === '300' && lte === '600') priceSelect.value = '300:600';
      else if (gte === '600' && lte === '1000') priceSelect.value = '600:1000';
      else if (gte === '1000' && !lte) priceSelect.value = 'gte:1000';

      if (priceSelect.value) priceSelect.classList.add('v8-subnav-select--active');

      priceSelect.addEventListener('change', function () {
        const val = this.value;
        const p = new URLSearchParams(params);
        p.delete('filter.v.price.gte');
        p.delete('filter.v.price.lte');
        if (val === 'lte:300') p.set('filter.v.price.lte', '300');
        else if (val === '300:600') { p.set('filter.v.price.gte', '300'); p.set('filter.v.price.lte', '600'); }
        else if (val === '600:1000') { p.set('filter.v.price.gte', '600'); p.set('filter.v.price.lte', '1000'); }
        else if (val === 'gte:1000') p.set('filter.v.price.gte', '1000');
        const qs = p.toString();
        window.location.href = basePath + (qs ? '?' + qs : '');
      });
    }

    // Sort select
    const sortSelect = document.getElementById('v8SortSelect');
    if (sortSelect) {
      const currentSort = params.get('sort_by');
      if (currentSort) {
        sortSelect.value = currentSort;
        sortSelect.classList.add('v8-subnav-select--active');
      }
      sortSelect.addEventListener('change', function () {
        const val = this.value;
        const p = new URLSearchParams(params);
        if (val) p.set('sort_by', val);
        else p.delete('sort_by');
        const qs = p.toString();
        window.location.href = basePath + (qs ? '?' + qs : '');
      });
    }
  }

  // ─── Spec-Filter: Dropdown Active-State + Multi-Tag Navigation ──────────────
  function initSpecFilterActive() {
    // URL can hold multiple tags joined by '+':
    // /collections/felgen/groesse:20zoll+lochkreis:5x127+farbe:bronze
    const parts = window.location.pathname.split('/');
    const colIdx = parts.indexOf('collections');
    // Everything after /collections/handle/ is the tag string
    const tagPart = colIdx >= 0 && parts[colIdx + 2]
      ? decodeURIComponent(parts[colIdx + 2])
      : '';
    const activeTags = tagPart ? tagPart.split('+') : [];
    // Base URL is /collections/handle without any tags
    const collectionBase = colIdx >= 0
      ? '/' + parts.slice(1, colIdx + 2).join('/')
      : '';

    document.querySelectorAll('.v8-spec-select').forEach(function (sel) {
      const base = sel.dataset.specBase || collectionBase;

      // Determine the tag-prefix this select manages (first non-empty option prefix)
      var firstVal = '';
      for (var i = 0; i < sel.options.length; i++) {
        if (sel.options[i].value) { firstVal = sel.options[i].value; break; }
      }
      var prefix = firstVal ? firstVal.split(':')[0] : null;

      // Find any active tag that belongs to this select's prefix group
      var activeForThis = null;
      for (var t = 0; t < activeTags.length; t++) {
        if (prefix && activeTags[t].indexOf(prefix + ':') === 0) {
          activeForThis = activeTags[t];
          break;
        }
      }
      if (activeForThis) {
        sel.value = activeForThis;
        sel.classList.add('v8-subnav-select--active');
      }

      // On change: rebuild tag list (replace this group's tag, keep others)
      sel.addEventListener('change', function () {
        var val = this.value;
        // Remove existing tags of this prefix, then add new one if selected
        var kept = activeTags.filter(function (t) {
          return !(prefix && t.indexOf(prefix + ':') === 0);
        });
        if (val) kept.push(val);
        var tagStr = kept.join('+');
        window.location.href = tagStr ? base + '/' + tagStr : base;
      });
    });
  }

  // ─── Fahrzeug-Filter (tag-basiert) ──────────────────────────────────────────
  function initVehicleFilter() {
    document.querySelectorAll('.v8-vehicle-select').forEach(function (sel) {
      // Aktuell aktives Fahrzeug-Tag aus URL lesen
      var path = window.location.pathname; // z.B. /collections/felgen/ford-f150
      var parts = path.split('/');
      var colIdx = parts.indexOf('collections');
      var activeTags = colIdx !== -1 && parts[colIdx + 2]
        ? parts[colIdx + 2].split('+')
        : [];

      // Fahrzeug-Tags haben kein Prefix — direkt matchen
      var vehicleOptions = Array.from(sel.options).map(function (o) { return o.value; });
      var activeVehicle = activeTags.find(function (t) { return vehicleOptions.indexOf(t) !== -1; });
      if (activeVehicle) {
        sel.value = activeVehicle;
        sel.classList.add('v8-subnav-select--active');
      }

      sel.addEventListener('change', function () {
        var val = this.value;
        // Andere aktive Tags (z.B. Größe, Lochkreis) behalten
        var otherTags = activeTags.filter(function (t) { return vehicleOptions.indexOf(t) === -1; });
        if (val) otherTags.unshift(val);
        var colBase = parts.slice(0, colIdx + 2).join('/');
        var tagStr  = otherTags.join('+');
        window.location.href = tagStr ? colBase + '/' + tagStr : colBase;
      });
    });
  }

  // ─── MwSt. + Versand Hinweis auf Produktseite (PDP) ─────────────────────────
  function initTaxHint() {
    // Nur auf Produktseiten ausführen
    if (!document.querySelector('.product-information')) return;

    // Preis-Element in der Details-Spalte finden
    var priceEl = document.querySelector('.product-information .price');
    if (!priceEl || priceEl.querySelector('.v8-tax-hint')) return;

    var hint = document.createElement('span');
    hint.className = 'v8-tax-hint';
    hint.innerHTML = 'inkl. MwSt. <a href="/policies/shipping-policy">zzgl. Versand</a>';
    priceEl.insertAdjacentElement('afterend', hint);
  }

  // ─── 11. Homepage: Speed-Line Flash auf Hero ─────────────────────────────────
  function initHomepageAnims() {
    var hero = document.querySelector('.v8-split-hero');
    if (!hero) return;

    // Speed-Line-Blitz beim ersten Laden
    var flash = document.createElement('div');
    flash.className = 'v8-speed-flash';
    hero.appendChild(flash);
    setTimeout(function () { if (flash.parentNode) flash.remove(); }, 1600);
  }

  // ─── 12. Parallax: Cinematic-Hintergrundbild scrollt langsamer ───────────────
  function initParallax() {
    var cinematic = document.querySelector('.v8-cinematic');
    if (!cinematic) return;

    var bg = cinematic.querySelector('.v8-cinematic__bg');
    var content = cinematic.querySelector('.v8-cinematic__content');
    if (!bg) return;

    // Content erst sichtbar machen wenn Section in View kommt
    if (content) {
      content.classList.add('v8-cinematic-content--hidden');
      var contentObs = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            // Gestaffeltes Einblenden der Content-Elemente
            var tag   = content.querySelector('.v8-cinematic__tag');
            var title = content.querySelector('.v8-cinematic__title');
            var cta   = content.querySelector('.v8-cinematic__cta');
            content.classList.remove('v8-cinematic-content--hidden');
            if (tag)   { tag.style.transitionDelay   = '0s'; }
            if (title) { title.style.transitionDelay  = '0.15s'; }
            if (cta)   { cta.style.transitionDelay    = '0.3s'; }
            contentObs.unobserve(entry.target);
          }
        });
      }, { threshold: 0.25 });
      contentObs.observe(cinematic);
    }

    // Parallax-Scroll
    var rafPending = false;
    function updateParallax() {
      var rect = cinematic.getBoundingClientRect();
      // 25% der Scroll-Geschwindigkeit — BG bewegt sich langsamer
      var offset = rect.top * 0.22;
      bg.style.transform = 'translateY(' + offset + 'px)';
      rafPending = false;
    }

    window.addEventListener('scroll', function () {
      if (!rafPending) {
        requestAnimationFrame(updateParallax);
        rafPending = true;
      }
    }, { passive: true });

    updateParallax();
  }

  // ─── 13. Scroll-Stagger: Cat-Rows, USPs und Stats ────────────────────────────
  function initScrollStagger() {
    if (!('IntersectionObserver' in window)) return;

    function stagger(selector, delayStep, axis) {
      var els = document.querySelectorAll(selector);
      if (!els.length) return;

      var translateProp = axis === 'x' ? 'translateX(-20px)' : 'translateY(18px)';
      els.forEach(function (el) {
        el.style.opacity = '0';
        el.style.transform = translateProp;
        el.style.transition = 'opacity 0.9s ease, transform 0.9s cubic-bezier(0.16, 1, 0.3, 1)';
      });

      var obs = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var idx = Array.from(els).indexOf(entry.target);
          var delay = idx * delayStep;
          setTimeout(function () {
            entry.target.style.opacity = '1';
            entry.target.style.transform = 'none';
          }, delay);
          obs.unobserve(entry.target);
        });
      }, { threshold: 0.18 });

      els.forEach(function (el) { obs.observe(el); });
    }

    stagger('.v8-cat-row', 140, 'x');  // Kategorien: von links
    stagger('.v8-usp',     180, 'y');  // USPs: von unten
    stagger('.v8-stat',    150, 'y');  // Stats: von unten
  }

  // ─── Light / Dark Mode Toggle ────────────────────────────────────────────────
  // Setzt data-theme="light"|"dark" auf <html>, speichert in localStorage.
  // Smooth-Transition via kurzlebige Klasse v8-theme-switching.
  // ─── Subnav Felgen-Dropdown Click-Toggle ──────────────────────────────────────
  // Ergänzt CSS-:hover mit Click/Touch-Support und schließt bei Klick außerhalb.
  function initSubnavDropdown() {
    var groups = document.querySelectorAll('.v8-shop-subnav__group');
    groups.forEach(function(group) {
      var trigger  = group.querySelector('.v8-shop-subnav__cat--parent');
      var dropdown = group.querySelector('.v8-shop-subnav__dropdown');
      if (!trigger || !dropdown) return;

      // Click auf Parent-Link → Dropdown toggeln (statt direkt navigieren)
      trigger.addEventListener('click', function(e) {
        var isOpen = group.classList.contains('v8-subnav-open');
        // Alle anderen schließen
        document.querySelectorAll('.v8-shop-subnav__group').forEach(function(g) {
          g.classList.remove('v8-subnav-open');
        });
        if (!isOpen) {
          e.preventDefault();
          group.classList.add('v8-subnav-open');
        }
      });
    });

    // Klick außerhalb → alle schließen
    document.addEventListener('click', function(e) {
      if (!e.target.closest('.v8-shop-subnav__group')) {
        document.querySelectorAll('.v8-shop-subnav__group').forEach(function(g) {
          g.classList.remove('v8-subnav-open');
        });
      }
    });
  }

  function initThemeToggle() {
    // data-theme bereits durch theme.liquid inline-Script gesetzt (FOUC-frei)
    // Button ist server-side in header-actions.liquid gerendert → nur Click-Handler
    var btn = document.getElementById('v8ThemeToggle');
    if (!btn || btn.dataset.v8Init) return;
    btn.dataset.v8Init = '1';

    btn.addEventListener('click', function () {
      var next = document.documentElement.getAttribute('data-theme') === 'dark' ? 'light' : 'dark';
      document.documentElement.setAttribute('data-theme', next);
      localStorage.setItem('v8-theme', next);
    });

    // System-Präferenz live verfolgen — nur wenn der User noch NIE manuell umgeschaltet hat
    if (window.matchMedia) {
      window.matchMedia('(prefers-color-scheme: light)').addEventListener('change', function(e) {
        try {
          if (!localStorage.getItem('v8-theme')) {
            document.documentElement.setAttribute('data-theme', e.matches ? 'light' : 'dark');
          }
        } catch(err) {}
      });
    }
  }

  // Scroll-Lock-Kompensation absichtlich entfernt:
  // Horizon dialog.js setzt body { position: fixed; top: -scrollY } selbst —
  // eine zusätzliche padding-right-Kompensation würde doppelt wirken und
  // das Layout beim Öffnen des Suche-Modals verschieben.
  function initScrollLockCompensation() { /* no-op */ }

  // ─── Logo-mask hero cursor parallax ─────────────────────────────────────────
  // Verschiebt die Logo-Maske (Truck sichtbar durch Logo-Form) sanft mit dem
  // Maus-Cursor — gibt dem Hero ein lebendiges, dreidimensionales Gefühl.
  // Lerp-Loop damit der Übergang butterweich bleibt.
  function initMaskedParallax() {
    var maskLayer = document.querySelector('.v8-logo-hero__mask-layer');
    if (!maskLayer) return;

    var targetX = 0, targetY = 0;
    var currentX = 0, currentY = 0;
    var maxShift = 14; // px in jede Richtung

    document.addEventListener('mousemove', function (e) {
      targetX = ((e.clientX / window.innerWidth)  - 0.5) * maxShift * 2;
      targetY = ((e.clientY / window.innerHeight) - 0.5) * maxShift * 2;
    });

    function tick() {
      currentX += (targetX - currentX) * 0.05;
      currentY += (targetY - currentY) * 0.05;
      maskLayer.style.transform = 'translate(' + currentX.toFixed(2) + 'px, ' + currentY.toFixed(2) + 'px)';
      requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  }

  // ─── Init ────────────────────────────────────────────────────────────────────
  function init() {
    initReveal();
    initCounters();
    initShopMega();
    initCardHover();
    initCursorGlow();
    initAnnouncementTicker();
    initTilt3D();
    initShowcaseSpotlight();
    initServiceCards();
    initVehicleDropdown();
    initSubnavControls();
    initSpecFilterActive();
    initVehicleFilter();
    initTaxHint();
    initHomepageAnims();
    initParallax();
    initScrollStagger();
    initMaskedParallax();
    initSubnavDropdown();
    initThemeToggle();
    initScrollLockCompensation();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init nach Shopify Section-Updates im Theme-Editor
  document.addEventListener('shopify:section:load', init);
})();
